import { Page, View, Text, Image } from '@react-pdf/renderer';
import { styles } from './styles';
import {
  Portfolio,
  PublicEventDetailsResponse,
  PublicSessionItem,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { replaceWithComponent } from '@utils/htmlParser';
import { getImageSrc } from '@utils/fn';
import { globalStyles } from '../../styles';
import { useMemo } from 'react';
import { SafeImage } from '../../components/SafeImage';
import { getSessionTime } from '@utils/time';

// Extend Day.js with needed plugins
dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

export interface AgendaBodyPagesProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  agendaPdfDates: string[];
  groupedAgendasSessions: PublicSessionItem[][];
  header: JSX.Element;
  footer: JSX.Element;
}

const AgendaBodyPages = ({
  event,
  coLocatedEvents,
  portfolios,
  agendaPdfDates,
  groupedAgendasSessions,
  header,
  footer,
}: AgendaBodyPagesProps) => {
  const { t, i18n } = useTranslation();

  const sortedGroupedAgendasSession = useMemo(() => {
    if (!groupedAgendasSessions) return [];

    return [...groupedAgendasSessions].sort((a, b) => {
      const dateA = new Date(a?.[0]?.start_date).getTime();
      const dateB = new Date(b?.[0]?.start_date).getTime();
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      // If start_date is equal, sort by priority (lowest first)
      const priorityA =
        typeof a?.[0]?.priority === 'number' ? a?.[0]?.priority : 9999;
      const priorityB =
        typeof b?.[0]?.priority === 'number' ? b?.[0]?.priority : 9999;
      return priorityA - priorityB;
    });
  }, [groupedAgendasSessions]);

  return (
    <Page size="A4" orientation="portrait" style={{ paddingBottom: 60 }}>
      {header}
      <View style={styles.body}>
        <Text
          style={{
            ...globalStyles.mediumTitle,
            color: event?.design.themeColor,
          }}
        >
          {t('agenda')}
        </Text>

        <View
          style={{
            ...styles.lineBar,
            borderTop: `4px solid ${event?.design?.themeColor || 'black'}`,
            width: '80px',
          }}
        ></View>

        <View style={styles.bodyContainer}>
          {agendaPdfDates?.map((date, index) => (
            <View key={date} style={styles.dayWrapper}>
              <Text
                style={{
                  ...styles.dayHeader,
                  backgroundColor: event?.design.themeColor,
                }}
              >
                {date}
              </Text>
              {sortedGroupedAgendasSession?.[index]?.map(session => {
                const formattedDate = dayjs
                  .utc(session?.start_date)
                  .locale(i18n.language)
                  .format('dddd D, MMM YYYY');

                return (
                  <View
                    style={styles.sessionWrapper}
                    key={session?.id}
                    wrap={false}
                  >
                    <View style={styles.sessionHeader}>
                      <View style={styles.eventTimeWrapper}>
                        <Text style={styles.eventTime}>
                          {`${getSessionTime(
                            session?.start_date,
                            session?.end_date,
                            event?.timezone
                          )}`}
                        </Text>
                      </View>
                      <View style={styles.sessionTitleWrapper}>
                        <Text style={styles.sessionTitle}>
                          {' '}
                          {session?.title}
                        </Text>
                      </View>
                    </View>

                    <Text style={styles.sessionTextTitle}>
                      {session?.title}
                    </Text>

                    {session?.speakers?.length > 0 && (
                      <View style={styles.speakersSection}>
                        <View style={styles.logoColumn}>
                          {session?.speakers
                            ?.filter(speaker => speaker?.company?.logo)
                            ?.map(speaker => {
                              return (
                                <SafeImage
                                  key={speaker.profile}
                                  src={getImageSrc(speaker?.company?.logo)}
                                  style={styles.companyLogo}
                                  debugLabel={`speaker-company-logo-${speaker.profile}`}
                                />
                              );
                            })}
                        </View>

                        <View style={styles.speakerGrid}>
                          {session?.speakers
                            ?.filter(speaker => speaker?.images?.picture)
                            ?.map((speaker, idx) => {
                              return (
                                <View
                                  key={speaker.profile}
                                  style={styles.speakerCard}
                                >
                                  <SafeImage
                                    src={getImageSrc(speaker?.images?.picture)}
                                    style={styles.speakerImage}
                                    debugLabel={`speaker-picture-${speaker.profile}`}
                                  />

                                  <View style={styles.speakerInfo}>
                                    <Text style={styles.speakerName}>
                                      {`${speaker.first_name} ${speaker.last_name}`}{' '}
                                    </Text>
                                    <Text style={styles.speakerTitle}>
                                      {speaker.job?.title}
                                    </Text>
                                  </View>
                                </View>
                              );
                            })}
                        </View>
                      </View>
                    )}

                    {typeof session?.description === 'string' && (
                      <Text style={styles.sessionDescription}>
                        {replaceWithComponent(session?.description ?? '')}
                      </Text>
                    )}

                    <View style={styles.formattedDateContainer}>
                      <Text style={styles.formattedDateText}>
                        {formattedDate}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </View>
      {footer}
    </Page>
  );
};

export default AgendaBodyPages;
